import AuthService from './AuthService'
import CacheService from './CacheService'
import { eventBus } from '@/utils/eventBus'

/**
 * Service pour gérer les badges via l'API backend
 * Remplace le système de badges basé sur localStorage
 * Optimisé avec cache pour réduire les appels API
 */
const BadgeService = {
  /**
   * Récupère l'ID de l'utilisateur connecté depuis le JWT
   * @returns {Promise<number>} L'ID de l'utilisateur
   */
  async getUserId() {
    try {
      const response = await AuthService.request('get', '/user/me/id')
      return response.data.user_id
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'ID utilisateur:', error)
      throw error
    }
  },

  /**
   * Récupère tous les badges de l'utilisateur avec sa progression
   * Utilise le cache pour éviter les appels API redondants
   * @param {boolean} forceRefresh - Force le rafraîchissement depuis l'API
   * @returns {Promise<{badges: Array, progress: number, level: number}>}
   */
  async getUserBadges(forceRefresh = false) {
    try {
      if (forceRefresh) {
        CacheService.invalidate('badges')
      }

      return await CacheService.getOrFetch('badges', async () => {
        const response = await AuthService.request('get', '/user/me/badges')
        return {
          badges: response.data.badges || [],
          progress: response.data.progress || 0,
          level: response.data.level || 1
        }
      })
    } catch (error) {
      console.error('Erreur lors du chargement des badges:', error)
      throw error
    }
  },

  /**
   * Récupère un badge spécifique
   * @param {number} badgeId - ID du badge (0-9)
   * @returns {Promise<Object>} Le badge
   */
  async getBadge(badgeId) {
    try {
      const response = await AuthService.request('get', `/user/me/badges/${badgeId}`)
      return response.data
    } catch (error) {
      console.error(`Erreur lors du chargement du badge ${badgeId}:`, error)
      throw error
    }
  },

  /**
   * Récupère la progression de l'utilisateur
   * @returns {Promise<Object>} Objet de progression
   */
  async getUserProgress() {
    try {
      const response = await AuthService.request('get', '/user/me/progress')
      return response.data
    } catch (error) {
      console.error('Erreur lors du chargement de la progression:', error)
      throw error
    }
  },

  /**
   * Débloque un badge
   * @param {number} badgeId - ID du badge à débloquer
   * @returns {Promise<Object>} Le badge débloqué
   */
  async unlockBadge(badgeId) {
    try {
      const response = await AuthService.request('post', '/user/me/badges/unlock', {
        badge_id: badgeId
      })

      // Invalider le cache et émettre un événement
      CacheService.invalidate('badges')
      CacheService.invalidate('gamesProgress')
      eventBus.emit('badge-unlocked', { badgeId, badge: response.data })

      return response.data
    } catch (error) {
      console.error(`Erreur lors du déblocage du badge ${badgeId}:`, error)
      throw error
    }
  },

  /**
   * Fait évoluer un badge (système de médailles cliquables)
   * @param {number} clickCount - Nombre de clics
   * @returns {Promise<{evolved: boolean, level: number, badge_image: string}>}
   */
  async evolveBadge(clickCount) {
    try {
      const response = await AuthService.request('post', '/user/me/badges/evolve', {
        click_count: clickCount
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'évolution du badge:', error)
      throw error
    }
  },

  /**
   * Recalcule la progression de l'utilisateur
   * @returns {Promise<{progress: number, level: number, updated_badges: Array}>}
   */
  async recalculateProgress() {
    try {
      const response = await AuthService.request('put', '/user/me/progress/recalculate')

      // Invalider le cache après recalcul
      CacheService.invalidate('badges')
      CacheService.invalidate('gamesProgress')

      return response.data
    } catch (error) {
      console.error('Erreur lors du recalcul de la progression:', error)
      throw error
    }
  },

  /**
   * Supprime un badge (admin uniquement normalement)
   * @param {number} badgeId - ID du badge à supprimer
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async deleteBadge(badgeId) {
    try {
      const response = await AuthService.request('delete', `/user/me/badges/${badgeId}`)
      return response.data
    } catch (error) {
      console.error(`Erreur lors de la suppression du badge ${badgeId}:`, error)
      throw error
    }
  },

  /**
   * Récupère toutes les définitions de badges (sans authentification)
   * @returns {Promise<Array>} Liste des définitions de badges
   */
  async getAllBadgeDefinitions() {
    try {
      const response = await AuthService.request('get', '/user/badges/all')
      return response.data.badges || []
    } catch (error) {
      console.error('Erreur lors du chargement des définitions de badges:', error)
      throw error
    }
  },

  /**
   * Map un nom de jeu vers l'ID de badge correspondant
   * @param {string} gameId - Identifiant du jeu
   * @returns {number|null} ID du badge ou null si non trouvé
   */
  getGameBadgeId(gameId) {
    const mapping = {
      'profile': 0,
      'speed': 1,
      'scenario': 2,
      'shapes': 3,
      'shape-sequence': 3,
      'skills': 4,
      'roue-des-competences': 4,
      'jobs': 5,
      'metiers': 5,
      'room-env': 6,
      'environment': 6,
      'abilities': 7,
      'cv': 8,
      'formation': 9
    }
    return mapping[gameId] || null
  },

  /**
   * Migre les badges du localStorage vers le backend
   * Cette fonction aide à la transition vers le système backend
   * @returns {Promise<void>}
   */
  async migrateLocalStorageBadges() {
    try {
      const savedBadges = localStorage.getItem('userBadges')
      if (!savedBadges) {
        console.log('Aucun badge à migrer depuis localStorage')
        return
      }

      const badgeData = JSON.parse(savedBadges)
      const unlockedBadges = badgeData.filter(badge => badge.unlocked)

      console.log(`Migration de ${unlockedBadges.length} badges débloqués...`)

      for (const badge of unlockedBadges) {
        try {
          await this.unlockBadge(badge.id)
          console.log(`Badge ${badge.id} (${badge.title}) migré avec succès`)
        } catch (error) {
          // Le badge est peut-être déjà débloqué dans le backend
          console.warn(`Impossible de migrer le badge ${badge.id}:`, error.message)
        }
      }

      // Sauvegarder une copie de backup avant suppression
      localStorage.setItem('userBadges_backup', savedBadges)
      localStorage.removeItem('userBadges')

      console.log('Migration terminée ! Backup sauvegardé dans userBadges_backup')
    } catch (error) {
      console.error('Erreur lors de la migration des badges:', error)
      throw error
    }
  },

  /**
   * Vérifie si l'utilisateur peut débloquer le badge "Tous les jeux finis"
   * @param {Array} badges - Liste des badges
   * @returns {boolean}
   */
  canUnlockAllGamesCompleted(badges) {
    const gameIds = [1, 2, 3, 4, 5, 7]
    return gameIds.every(id => {
      const badge = badges.find(b => b.id === id)
      return badge && badge.unlocked
    })
  },

  /**
   * Vérifie si l'utilisateur peut débloquer le badge CV
   * @param {Array} badges - Liste des badges
   * @returns {boolean}
   */
  canUnlockCV(badges) {
    const gameIds = [1, 2, 3, 4, 5, 7]
    return gameIds.every(id => {
      const badge = badges.find(b => b.id === id)
      return badge && badge.unlocked
    })
  },

  /**
   * Vérifie si l'utilisateur peut débloquer le badge Formation
   * @param {Array} badges - Liste des badges
   * @returns {boolean}
   */
  canUnlockFormation(badges) {
    const cvBadge = badges.find(b => b.id === 6)
    return cvBadge && cvBadge.unlocked
  }
}

export default BadgeService
